import { Box, Stack } from '@mantine/core';
import { SettingsSelect } from '@repo/common/components';
import type { PreferenceOption } from '@repo/common/constants/integration/integrations.preferences';
import { Text, Title } from '@repo/foundations';
import { upperFirst } from 'lodash-es';
import { memo, useCallback } from 'react';
import type { IIntegration } from '../../../../../api';
import {
	integrationsQueryKeyFactory,
	queryClient,
	useUpdateIntegration,
} from '../../../../../api';
import type { WithOnlyIdRequired } from '../../../../../lib/typescript';
import { openConfirmModal } from '../../../../ModalManager';

interface ImportMetadataPreferencesProps {
	integration: IIntegration;
	metadataManagementPreferences: PreferenceOption[];
}

function getImportPreferenceValue(
	integration: IIntegration,
	preventField: string,
	forceField: string
): 'never' | 'smart' | 'always' {
	if (integration[preventField as keyof IIntegration] === true) {
		return 'never';
	} else if (integration[forceField as keyof IIntegration] === true) {
		return 'always';
	}
	return 'smart';
}

const OPTIONS = [
	{
		value: 'never',
		label: 'Never',
		description: 'Never imported from the source',
	},
	{
		value: 'smart',
		label: 'Smart',
		description: 'Imported from the source until modified in Secoda',
	},
	{
		value: 'always',
		label: 'Always',
		description:
			'Always imported from the source and overwrites the value in Secoda',
	},
];

export const ImportMetadataPreferences = memo(
	({
		integration,
		metadataManagementPreferences,
	}: ImportMetadataPreferencesProps) => {
		const { mutateAsync: updateIntegration } = useUpdateIntegration({});

		const handlePreferenceRadioChange = useCallback(
			(value: string, forceField: string, preventField: string) => {
				const integrationUpdate: WithOnlyIdRequired<IIntegration> = {
					id: integration.id,
				};

				if (value === 'never') {
					integrationUpdate[
						forceField as keyof WithOnlyIdRequired<IIntegration>
					] = false as never;
					integrationUpdate[
						preventField as keyof WithOnlyIdRequired<IIntegration>
					] = true as never;

					updateIntegration(
						{ data: integrationUpdate },
						{
							onSuccess: () => {
								queryClient.invalidateQueries({
									queryKey: integrationsQueryKeyFactory.byId(integration.id),
								});
							},
						}
					);
				} else if (value === 'always') {
					openConfirmModal({
						title: 'Are you sure?',
						children: `Are you sure you want to always force this preference? This may overwrite existing values.`,
						onConfirm: () => {
							const confirmData: WithOnlyIdRequired<IIntegration> = {
								id: integration.id,
								[forceField as keyof WithOnlyIdRequired<IIntegration>]:
									true as never,
								[preventField as keyof WithOnlyIdRequired<IIntegration>]:
									false as never,
							};

							updateIntegration(
								{ data: confirmData },
								{
									onSuccess: () => {
										queryClient.invalidateQueries({
											queryKey: integrationsQueryKeyFactory.byId(
												integration.id
											),
										});
									},
								}
							);
						},
					});
				} else {
					integrationUpdate[
						forceField as keyof WithOnlyIdRequired<IIntegration>
					] = false as never;
					integrationUpdate[
						preventField as keyof WithOnlyIdRequired<IIntegration>
					] = false as never;

					updateIntegration(
						{ data: integrationUpdate },
						{
							onSuccess: () => {
								queryClient.invalidateQueries({
									queryKey: integrationsQueryKeyFactory.byId(integration.id),
								});
							},
						}
					);
				}
			},
			[integration.id, updateIntegration]
		);

		return (
			<Stack spacing="md">
				<Stack spacing={0}>
					<Title size="md">Property import</Title>
					<Text size="sm" color="text/secondary/default">
						Select how properties are imported from{' '}
						{upperFirst(integration.type)}
					</Text>
				</Stack>
				<Box>
					{metadataManagementPreferences?.find(
						(option) =>
							option.integrationFieldName === 'force_source_description'
					) && (
						<SettingsSelect
							title="Import descriptions"
							options={OPTIONS}
							value={getImportPreferenceValue(
								integration,
								'prevent_source_description',
								'force_source_description'
							)}
							onChange={(value: string | null) => {
								if (!value) {
									return;
								}
								handlePreferenceRadioChange(
									value,
									'force_source_description',
									'prevent_source_description'
								);
							}}
						/>
					)}
					{metadataManagementPreferences?.find(
						(option) => option.integrationFieldName === 'force_source_owners'
					) && (
						<SettingsSelect
							title="Import owners"
							options={OPTIONS}
							value={getImportPreferenceValue(
								integration,
								'prevent_source_owners',
								'force_source_owners'
							)}
							onChange={(value: string | null) => {
								if (!value) {
									return;
								}
								handlePreferenceRadioChange(
									value,
									'force_source_owners',
									'prevent_source_owners'
								);
							}}
						/>
					)}
					{metadataManagementPreferences?.find(
						(option) => option.integrationFieldName === 'force_source_tags'
					) && (
						<SettingsSelect
							title="Import tags"
							options={OPTIONS}
							value={getImportPreferenceValue(
								integration,
								'prevent_source_tags',
								'force_source_tags'
							)}
							onChange={(value: string | null) => {
								if (!value) {
									return;
								}
								handlePreferenceRadioChange(
									value,
									'force_source_tags',
									'prevent_source_tags'
								);
							}}
						/>
					)}
				</Box>
			</Stack>
		);
	}
);
